import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isInvoice = params.get("invoice") === "1";
  const invoiceOrder = params.get("order");
  const [status, setStatus] = useState(isInvoice ? "invoice" : "checking");
  const [orderNumber, setOrderNumber] = useState(invoiceOrder || null);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.order_number) setOrderNumber(data.order_number);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch {}
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 pt-16" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-lg w-full border border-white/10 bg-card p-10 text-center"
      >
        {status === "checking" && (
          <>
            <Loader2 className="animate-spin text-kinetic mx-auto" size={40} />
            <h1 className="font-display text-3xl uppercase mt-6" data-testid="payment-status-heading">Confirming payment…</h1>
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-4">Hold tight, talking to Stripe.</p>
          </>
        )}
        {(status === "paid" || status === "invoice") && (
          <>
            <CheckCircle2 className="text-emerald-500 mx-auto" size={44} />
            <h1 className="font-display text-3xl uppercase mt-6" data-testid="payment-status-heading">
              {status === "paid" ? "Payment locked in" : "Order placed"}
            </h1>
            {orderNumber && (
              <p className="font-mono text-sm text-kinetic uppercase tracking-[0.25em] mt-4" data-testid="order-number-display">{orderNumber}</p>
            )}
            <p className="text-zinc-400 text-sm leading-relaxed mt-4">
              {status === "paid"
                ? "Your gear is entering the production line. Track it live from your account."
                : "We've received your order. An invoice is on its way to your inbox — 14 day terms."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Link to="/track" data-testid="success-track-button" className="btn-kinetic">Track order</Link>
              <Link to="/shop" data-testid="success-shop-button" className="btn-ghost">Keep shopping</Link>
            </div>
          </>
        )}
        {status === "timeout" && (
          <>
            <h1 className="font-display text-3xl uppercase" data-testid="payment-status-heading">Still processing</h1>
            <p className="text-zinc-400 text-sm mt-4">Payment is taking longer than usual. Check your account or track your order shortly.</p>
            <Link to="/track" className="btn-kinetic mt-8" data-testid="success-track-button">Track order</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
